import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import {
    GoogleMap,
    OverlayView,
    Polygon,
    Polyline,
    MarkerF,
} from '@react-google-maps/api';

// Components
import GoogleMapsLoadScriptEnabler from "../components/GoogleMapsLoadScript";

// Handler
import { MapInitialiserWithVehicleAndGeofence } from '../handlers/MapInitialiserWithVehicleAndGeofence'
import { UnfocusMap } from '../handlers/UnfocusMap'
import { geofenceTemplates_handler } from '../../../../handlers'

// Method
import { getLatLngCenter } from '../../methods'

// Redux Action 
import * as ACTIONS from '../../../../services/redux/actions'

// Styles
import "../gmaps.css";

const {
    returnLatestGeofenceTemplateColorForGeoID
} = geofenceTemplates_handler

const GoogleMapsZone = () => {

    const dispatch = useDispatch();

    // Redux Store
    const vehicles = useSelector(state => state.containers.v3.vehicles)
    const geofences = useSelector(state => state.containers.v3.geofences)
    const geofenceTemplates = useSelector(state => state.containers.v3.geofenceTemplates)
    const mapControl = useSelector(state => state.containers.v3.mapControl)

    // State
    const [mapRef, setMapRef] = useState(null);
    const [thisGeofence, setThisGeofence] = useState(mapControl.geofence);

    // Drawing state
    const [isDrawingMode, setIsDrawingMode] = useState(true);
    const [draftPoints, setDraftPoints] = useState([]);       // titik-titik yang sudah diklik
    const [mouseLatLng, setMouseLatLng] = useState(null);     // posisi kursor live (untuk preview garis)

    MapInitialiserWithVehicleAndGeofence(vehicles, geofences, mapControl, mapRef)

    // ─── Cursor peta ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (mapRef) {
            mapRef.setOptions({
                draggableCursor: isDrawingMode ? 'crosshair' : null,
            });
        }
    }, [isDrawingMode, mapRef]);

    // ─── Klik peta: tambah titik polygon ────────────────────────────────────────
    const handleMapClick = useCallback((e) => {
        if (!isDrawingMode) return;

        const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setDraftPoints(prev => [...prev, point]);
    }, [isDrawingMode]);

    // ─── Gerak mouse: update preview garis ──────────────────────────────────────
    const handleMouseMove = useCallback((e) => {
        if (!isDrawingMode || draftPoints.length === 0) return;
        setMouseLatLng({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }, [isDrawingMode, draftPoints.length]);

    // ─── Double klik: selesaikan polygon ────────────────────────────────────────
    const handleMapDblClick = useCallback(() => {
        if (!isDrawingMode) return;
        if (draftPoints.length < 3) {
            alert('Polygon minimal membutuhkan 3 titik.');
            return;
        }

        const newGeofence = {
            ...mapControl.geofence,
            coordinates: draftPoints,
        };

        dispatch(ACTIONS.v3_mapControl.set_new_geofence_from_map(newGeofence));

        // Reset drawing state
        setDraftPoints([]);
        setMouseLatLng(null);
        setIsDrawingMode(false);
    }, [isDrawingMode, draftPoints, mapControl.geofence, dispatch]);

    // ─── Batalkan drawing ────────────────────────────────────────────────────────
    const cancelDrawing = useCallback(() => {
        setDraftPoints([]);
        setMouseLatLng(null);
        setIsDrawingMode(false);
    }, []);

    // ─── Mulai drawing baru ──────────────────────────────────────────────────────
    const startDrawing = useCallback(() => {
        setDraftPoints([]);
        setMouseLatLng(null);
        setIsDrawingMode(true);
    }, []);

    // ─── Deep Effect ─────────────────────────────────────────────────────────────
    const useDeepEffect = (fn, deps) => {
        const isFirst = useRef(true);
        const prevDeps = useRef(deps);

        useEffect(() => {
            const isSame = prevDeps.current.every((obj, index) =>
                JSON.parse(JSON.stringify(obj)) === JSON.parse(JSON.stringify(deps[index]))
            );

            if (isFirst.current || !isSame) {
                fn();
            }

            isFirst.current = false;
            prevDeps.current = deps;
        }, [fn, deps]);
    }

    useDeepEffect(() => {
        setThisGeofence(mapControl.geofence)
    }, [mapControl.geofence])

    useEffect(() => {
        if (mapRef && mapControl.geofence && mapControl.geofence.coordinates && mapControl.geofence.coordinates.length) {
            const bounds = new window.google.maps.LatLngBounds();
            mapControl.geofence.coordinates.forEach(c => bounds.extend(c));
            mapRef.fitBounds(bounds);
        }
    }, [mapControl, mapRef])

    // ─── Preview: garis dari titik terakhir ke kursor mouse ──────────────────────
    const previewPath = draftPoints.length > 0 && mouseLatLng
        ? [...draftPoints, mouseLatLng]
        : null;

    return (
        <div style={{ position: 'relative' }}>

            {/* ── Toolbar Drawing (pengganti UI DrawingManager) ── */}
            <div
                style={{
                    position: 'absolute',
                    top: 10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'white',
                    padding: '6px 12px',
                    borderRadius: 4,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    fontSize: 13,
                }}
            >
                <button
                    title="Gambar Polygon"
                    onClick={startDrawing}
                    style={{
                        padding: '4px 12px',
                        cursor: 'pointer',
                        background: isDrawingMode ? '#1a73e8' : '#fff',
                        color: isDrawingMode ? '#fff' : '#444',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        fontWeight: isDrawingMode ? 'bold' : 'normal',
                    }}
                >
                    ⬠ Polygon
                </button>

                <button
                    title="Mode Geser Peta"
                    onClick={cancelDrawing}
                    style={{
                        padding: '4px 12px',
                        cursor: 'pointer',
                        background: !isDrawingMode ? '#1a73e8' : '#fff',
                        color: !isDrawingMode ? '#fff' : '#444',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        fontWeight: !isDrawingMode ? 'bold' : 'normal',
                    }}
                >
                    ✋ Geser
                </button>

                {/* Hint saat sedang drawing */}
                {isDrawingMode && (
                    <span style={{ color: '#555', marginLeft: 8 }}>
                        {draftPoints.length === 0
                            ? 'Klik untuk mulai menggambar'
                            : draftPoints.length < 3
                                ? `${draftPoints.length} titik — klik untuk lanjut`
                                : `${draftPoints.length} titik — klik dua kali untuk selesai`
                        }
                    </span>
                )}
            </div>

            <GoogleMap
                id='googles-map'
                mapContainerStyle={{ height: '100vh' }}
                onLoad={ref => setMapRef(ref)}
                onClick={handleMapClick}
                onMouseMove={handleMouseMove}
                onDblClick={handleMapDblClick}
                onDragStart={() => { UnfocusMap(dispatch) }}
                onDragEnd={() => {
                    if (mapRef) {
                        const lat = mapRef.getCenter().lat();
                        const lng = mapRef.getCenter().lng();
                        const zoom = mapRef.getZoom();
                        mapRef.setCenter({ lat, lng });
                        mapRef.setZoom(zoom);
                    }
                }}
            >
                {/* ── Polygon tersimpan ── */}
                {thisGeofence && thisGeofence.coordinates && (
                    <div>
                        <Polygon
                            path={thisGeofence.coordinates}
                            options={{
                                strokeColor: returnLatestGeofenceTemplateColorForGeoID(geofenceTemplates, thisGeofence.geoid),
                            }}
                        />

                        <OverlayView
                            options={{ disableAutoPan: true }}
                            position={getLatLngCenter(thisGeofence.coordinates)}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            getPixelPositionOffset={(width, height) => ({
                                x: -(width / 2),
                                y: -height - 3,
                            })}
                        >
                            <div
                                style={{
                                    background: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '10px',
                                    paddingLeft: 10,
                                    paddingRight: 10,
                                    paddingTop: 8,
                                    paddingBottom: 1,
                                }}
                            >
                                <h4>{thisGeofence.name}</h4>
                                <div>{thisGeofence.comment}</div>
                            </div>
                        </OverlayView>
                    </div>
                )}

                {/* ── Draft polygon (sedang digambar) ── */}
                {draftPoints.length >= 2 && (
                    <Polygon
                        path={draftPoints}
                        options={{
                            strokeColor: '#ff0000',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: '#ff0000',
                            fillOpacity: 0.15,
                        }}
                    />
                )}

                {/* ── Garis preview dari titik terakhir ke kursor ── */}
                {previewPath && (
                    <Polyline
                        path={previewPath}
                        options={{
                            strokeColor: '#ff0000',
                            strokeOpacity: 0.6,
                            strokeWeight: 2,
                            strokeDashArray: [5, 5],
                        }}
                    />
                )}

                {/* ── Titik-titik draft (marker kecil) ── */}
                {draftPoints.map((point, index) => (
                    <MarkerF
                        key={index}
                        position={point}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 5,
                            fillColor: '#ff0000',
                            fillOpacity: 1,
                            strokeColor: '#fff',
                            strokeWeight: 1.5,
                        }}
                    />
                ))}

            </GoogleMap>
        </div>
    )
}

export default GoogleMapsLoadScriptEnabler(GoogleMapsZone)